<template>
    <v-container fluid class="main-container d-flex flex-column pa-4">
        <v-card class="d-flex flex-column flex-grow-1">
            <v-card-title class="d-flex pt-6">
                <v-select class="pr-4" density="compact" base-color="primary" :ref="el => customer.selector.component = el"
                    :label="$t('field.customer')" v-model="customer.selector.selectedItem"
                    @update:model-value="onCustomerSelectorChange" :items="customers" item-title="name" item-value="id"
                    variant="outlined" return-object></v-select>
                <v-select class="" density="compact" base-color="primary" :ref="el => distributor.selector.component = el"
                    :label="$t('field.distributor')" v-model="distributor.selector.selectedItem"
                    @update:model-value="onDistributorSelectorChange" :items="distributors" item-title="name"
                    item-value="id" :disabled="distributor.selector.disabled" v-show="!hasDistributor"
                    variant="outlined" return-object></v-select>

                <v-spacer />

                <v-btn class="align-center mr-2" color="secondary" density="default" rounded="rounded-md"
                    v-show="buttons.createUser.show" :prepend-icon="icons.mdiPlus" @click="createUserAdmin"
                    :disabled="buttons.createUser.disabled">{{ $t('action.newUser') }}</v-btn>
                <v-btn class="align-center mr-2" color="secondary" density="default" rounded="rounded-md"
                    v-show="buttons.refresh.show" :prepend-icon="icons.mdiRefresh" @click="refreshUserCache"
                    :disabled="buttons.refresh.disabled">{{ $t('action.refreshUser') }}</v-btn>
                <v-btn class="align-center mr-2" color="secondary" density="default" rounded="rounded-md"
                    v-show="buttons.save.show" :prepend-icon="icons.mdiContentSaveOutline" @click="saveUsers"
                    :disabled="!hasPendingChanges">{{ $t('action.save') }}</v-btn>
            </v-card-title>

            <v-container fluid class="fill-height d-flex flex-column flex-grow-1">
                <AgGrid class="flex-grow-1" ref="userGrid" :fetchUrl="fetchUrl" :columnDefs="customerColumnDefs"
                    :gridParams="gridParams" :refresh="userRefresh"
                    :domLayout="user.grid.domLayout"/>
            </v-container>
        </v-card>

        <v-dialog v-model="userAdminDialog.visible" width="560px" min-width="560px">
            <v-card class="overflow-visible pt-16">
                <v-form @submit.prevent="saveUserAdminDetail" v-model="vformModel">

                    <v-card-item class="ma-auto overflow-visible">
                        <v-avatar size="190" color="primary" class="position-absolute dialog-picture elevation-3" border="border-color xl" >
                            <span class="text-display-large text-white">{{ userAdminInitials }}</span>
                        </v-avatar>
                    </v-card-item>



                    <v-card-text>
                        <v-container>
                            <v-text-field v-model="userAdminDialog.userName" :label="$t('field.userName')"
                                :prepend-inner-icon="icons.mdiAccount" icon-color="primary" base-color="primary" variant="outlined" required :rules="[rules.required]"
                                maxlength="50" counter="50" />

                            <v-text-field v-model="userAdminDialog.firstName" :label="$t('field.firstName')"
                                :prepend-inner-icon="icons.mdiAccount" icon-color="primary" base-color="primary" variant="outlined" required :rules="[rules.required]"
                                maxlength="50" counter="50" />

                            <v-text-field v-model="userAdminDialog.lastName" :label="$t('field.lastName')"
                                :prepend-inner-icon="icons.mdiAccount" icon-color="primary" base-color="primary" variant="outlined" required :rules="[rules.required]"
                                maxlength="50" counter="50" />

                            <v-text-field v-model="userAdminDialog.email" :label="$t('field.email')"
                                :prepend-inner-icon="icons.mdiAt" icon-color="primary" base-color="primary" variant="outlined"  required :rules="[rules.required, rules.email]"
                                type="email" maxlength="50" counter="50" />

                            <v-divider class="mb-4" />

                            <v-text-field v-model="userAdminDialog.newPassword" :label="$t('field.newPassword')"
                                :prepend-inner-icon="icons.mdiLock" icon-color="primary" base-color="primary" variant="outlined" type="password" :rules="[rules.required]"
                                :required="userAdminDialog.idSelected == 0" />

                            <v-text-field v-model="userAdminDialog.confirmNewPassword"
                                :label="$t('field.confirmNewPassword')" :prepend-inner-icon="icons.mdiLock" icon-color="primary" base-color="primary" :rules="[rules.required]"
                                variant="outlined" type="password" :required="userAdminDialog.idSelected == 0"
                                :error="userAdminPasswordMismatch"
                                :error-messages="userAdminPasswordMismatch ? [$t('toast.loginPasswordMismatch')] : []" />
                        </v-container>
                    </v-card-text>

                    <v-divider />

                    <v-card-actions class="justify-end">
                        <v-btn variant="text" color="error" @click="userAdminDialog.visible = false">
                            {{ $t('action.cancel') }}
                        </v-btn>
                        <v-btn variant="text" color="primary" type="submit" :disabled="!isUserAdminValid && !vformModel">
                            {{ $t('action.ok') }}
                        </v-btn>
                    </v-card-actions>

                </v-form>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
    import { computed, inject, reactive, ref, useTemplateRef, onBeforeMount } from 'vue';
    import AgGrid from '@/components/AgGrid.vue';
    import { toast } from 'vue3-toastify';
    import { useControlStore } from '@/stores/controlStore';
    import { useLogStore } from '@/stores/logStore';
    import { useUserStore } from '@/stores/userStore';
    import {
        mdiPlus,
        mdiRefresh,
        mdiContentSaveOutline,
        mdiAccount,
        mdiAt,
        mdiLock,
    } from '@mdi/js';

    const urlWebAPI = inject('urlWebAPI');
    const translate = inject('translate');
    const assortUrl = inject('assortUrl');
    const backOfficeUrl = inject('backOfficeUrl');
    const pspUrl = inject('pspUrl');
    const portalUrl = inject('portalUrl');

    const controlStore = useControlStore();
    const logStore = useLogStore();
    const userStore = useUserStore();

    const icons = {
        mdiPlus,
        mdiRefresh,
        mdiContentSaveOutline,
        mdiAccount,
        mdiAt,
        mdiLock,
    };

    const vformModel = ref(false);
    const rules = {
        required: v => (v !== null && v !== '' && typeof v !== 'undefined') || translate('required', 'toast'),
        email: v => checkEmailValidation(v) || translate('mailAlreadyTaken', 'toast')
    };
    const buttons = reactive({
        refresh: {
            disabled: true,
            show: false,
        },
        save: {
            show: true,
        },
        createUser: {
            disabled: false,
            show: false,
        },
    });

    const customers = ref([]);
    const customer = reactive({
        selector: {
            component: null,
            selectedItem: null,
            disabled: false,
        },
    });

    const distributors = ref([]);
    const distributor = reactive({
        selector: {
            component: null,
            selectedItem: null,
            disabled: false,
        },
    });

    const hasDistributor = userStore.getUser().hasDistributor;

    const usersAdmin = ref([]);
    const user = reactive({
        grid: {
            domLayout: "normal",
        },
    });

    const userAdminDialog = reactive({
        visible: false,
        idSelected: 0,
        userName: '',
        firstName: '',
        lastName: '',
        email: '',
        newPassword: '',
        confirmNewPassword: '',
    });

    const accessLevels = [
        {
            id: 0,
            name: 'Regular',
        },
        {
            id: 125,
            name: 'EdiSet',
        },
        {
            id: 150,
            name: 'Admin',
        },
        {
            id: 200,
            name: 'MiddleOffice',
        },
        {
            id: 220,
            name: 'MiddleOfficeAdmin',
        },
        {
            id: 255,
            name: 'WorldMaster',
        }
    ];
    
    const userGridRef = useTemplateRef('userGrid');
    const fetchUrl = 'GetUsers';
    const customerColumnDefs = [
        {
            field: 'infos',
            headerName: translate('infos', 'column'),
            children: [
                { field: 'id', hide: false, suppressHeaderMenuButton: true },
                {
                    field: 'userName',
                    headerName: translate('userName', 'column'),
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'firstName',
                    headerName: translate('firstName', 'column'),
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'lastName',
                    headerName: translate('lastName', 'column'),
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'accessLevel',
                    headerName: translate('accessLevel', 'column'),
                    hide: true,
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'activeFlag',
                    headerName: translate('active', 'column'),
                    type: 'booleanEditable',
                    sortable: false,
                    headerComponentParams: {
                        innerHeaderComponent: 'activeUserInnerHeaderComponent',
                        innerHeaderComponentParams: { columnName: translate('active', 'column')},
                    },
                    suppressHeaderMenuButton: true
                },
            ],
        },
        {
            field: 'apps',
            headerName: translate('apps', 'column'),
            children: [
                {
                    field: 'tokenECOS',
                    headerName: 'Portail eCOS',
                    width: 150,
                    maxWidth: 150,
                    resizable: false,
                    sortable: false,
                    cellRenderer: 'tokenComponent',
                    cellRendererParams: {
                        pspUrl: pspUrl,
                    },
                    filter: false,
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'tokenASSFO',
                    headerName: 'Assort',
                    width: 150,
                    maxWidth: 150,
                    resizable: false,
                    cellRenderer: 'tokenComponent',
                    cellRendererParams: {
                        pspUrl: pspUrl,
                    },
                    filterParams: {
                        cellRenderer: 'tokenComponent',
                    },
                    columnGroupShow: 'open',
                    hide: true,
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'tokenASSBO',
                    headerName: 'BackOffice',
                    width: 150,
                    maxWidth: 150,
                    resizable: false,
                    cellRenderer: 'tokenComponent',
                    cellRendererParams: {
                        pspUrl: pspUrl,
                    },
                    filterParams: {
                        cellRenderer: 'tokenComponent',
                    },
                    columnGroupShow: 'open',
                    hide: true,
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'tokenPSPFO',
                    headerName: 'PSP',
                    width: 150,
                    maxWidth: 150,
                    resizable: false,
                    cellRenderer: 'tokenComponent',
                    cellRendererParams: {
                        pspUrl: pspUrl,
                    },
                    filterParams: {
                        cellRenderer: 'tokenComponent',
                    },
                    columnGroupShow: 'open',
                    hide: true,
                    suppressHeaderMenuButton: true
                },
            ],
        },
    ];

    const distributorColumnDefs = [
        {
            field: 'infos',
            headerName: translate('infos', 'column'),
            children: [
                { field: 'id', hide: false },
                {
                    field: 'userName',
                    headerName: translate('userName', 'column'),
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'firstName',
                    headerName: translate('firstName', 'column'),
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'lastName',
                    headerName: translate('lastName', 'column'),
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'email',
                    headerName: translate('email', 'column'),
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'accessLevel',
                    headerName: translate('accessLevel', 'column'),
                    hide: true,
                    suppressHeaderMenuButton: true
                },
                {
                    field: 'activeFlag',
                    headerName: translate('active', 'column'),
                    type: 'boolean',
                    editable: false,
                    sortable: false,
                    suppressHeaderMenuButton: true,
                },
            ],
        },
        {
            field: 'actions',
            headerName: 'Actions',
            children: [
                { field: 'edit', headerName: '', type: 'editAction', suppressHeaderMenuButton: true},
                { field: 'delete', headerName: '', type: 'deleteAction', suppressHeaderMenuButton: true }
            ],
        },
    ];

    const gridParams = reactive({
        token: userStore.getUser().token,
        customerReference: '',
        onlyAdmin: true,
        allApps: false,
        distributorId: 0,
    });

    const userRefresh = ref(false);

    const rowsUpdated = ref([]);

    const hasPendingChanges = computed(() => rowsUpdated.value.length > 0);


    // AgGrid.vue reaches into this component through the $parent chain (getParentContext /
    // processData), which for a <script setup> component only sees what defineExpose() exposes.
    defineExpose({ onCellClicked, rowsUpdated, usersAdmin });

    onBeforeMount(() => {
            getCustomers().then(() => {
                getDistributors().then(() => {
                    setActiveValues();
                });
            });
        })


    const userAdminInitials = computed(() => {
        if (userAdminDialog.firstName == '' || userAdminDialog.lastName == '')
            return '+';

        return (userAdminDialog.firstName.substr(0, 1) + userAdminDialog.lastName.substr(0, 1)).toUpperCase();
    });

    const userAdminPasswordMismatch = computed(() => {
        return userAdminDialog.newPassword != '' && userAdminDialog.confirmNewPassword != '' && userAdminDialog.newPassword != userAdminDialog.confirmNewPassword;
    });

    const isUserAdminValid = computed(() => {
        var newPasswordNotMismatch = userAdminDialog.newPassword != '' && userAdminDialog.confirmNewPassword != '' && userAdminDialog.newPassword == userAdminDialog.confirmNewPassword;
        var passwordValidation = (userAdminDialog.idSelected > 0 && userAdminDialog.newPassword == '') || newPasswordNotMismatch;

        return userAdminDialog.userName != '' && userAdminDialog.firstName != '' && userAdminDialog.lastName != '' && userAdminDialog.email != '' && passwordValidation && isEmailAvailable(userAdminDialog.email);
    });

    function setActiveValues(){
        const activeCustomer = controlStore.getActiveCustomer();
        const activeDistributor = controlStore.getActiveDistributor();

        if(activeCustomer){
            customer.selector.component.$emit('update:modelValue', activeCustomer);
        }
        else if(activeDistributor){
            distributor.selector.component.$emit('update:modelValue', activeDistributor);
        }
    }

    function onCustomerSelectorChange() {
        if (customer.selector.selectedItem != null) {
            var gridApi = userGridRef.value.gridRef.api;
            gridApi.setGridOption('loading', true);
            if (gridApi != null) {
                gridApi.setGridOption('rowData', []);
                gridApi.setGridOption('loading', true);
            }

            controlStore.setActiveCustomer(customer.selector.selectedItem);
            controlStore.setActiveDistributor(null);

            getLastCustomerAgUsersUpdate();

            if (customer.selector.selectedItem.name != 'EXTERNIS')
                buttons.createUser.show = false;
            else
                buttons.createUser.show = true;

            gridParams.customerReference = customer.selector.selectedItem.reference;
            gridParams.distributorId = null;
            distributor.selector.component?.reset();
            userGridRef.value.getGridData().then(() => {
                if(gridApi != null && !gridApi.isDestroyed()){
                    endLoadUsers(gridApi);
                    refineGridColumns(gridApi, false);
                    gridApi.setGridOption('loading', false);
                }
            })
        }
    }

    function onDistributorSelectorChange() {
        userGridRef.value?.gridRef.api.setGridOption('loading', true);
        if (distributor.selector.selectedItem != null) {
            var gridApi = userGridRef.value.gridRef.api;

            if (gridApi != null) {
                gridApi.setGridOption('rowData', []);
                gridApi.setGridOption('loading', true);
            }

            controlStore.setActiveDistributor(distributor.selector.selectedItem);
            controlStore.setActiveCustomer(null);

            getLastCustomerAgUsersUpdate();

            buttons.createUser.show = true;

            gridParams.distributorId = distributor.selector.selectedItem.id;
            gridParams.customerReference = null;
            customer.selector.component?.reset();
            userGridRef.value.getGridData().then(() => {
                if(gridApi != null && !gridApi.isDestroyed()){
                    endLoadUsers(gridApi);
                    refineGridColumns(gridApi, false);
                    gridApi.setGridOption('loading', false);
                }
            })
        }
    }

    function onCellClicked(params) {
        if (typeof params.data != 'undefined' && typeof params.colDef.field != 'undefined') {
            // APPS TOKEN REDIRECT
            if (params.colDef.field.indexOf('token') > -1) {

                // ASSFO & ASSBO
                if ((params.colDef.field.indexOf('ASSFO') > -1 || params.colDef.field.indexOf('ASSBO') > -1) && params.data[params.colDef.field] != '') {
                    var redirect = '';

                    if (params.colDef.field.indexOf('ASSFO') > -1)
                        redirect = assortUrl;

                    if (params.colDef.field.indexOf('ASSBO') > -1)
                        redirect = backOfficeUrl;

                    $.redirectPost(redirect, { token: params.data[params.colDef.field], action: '' });
                }

                // WEBAPP
                else if (params.colDef.field.indexOf('ECOS') > -1)
                    portalRedirect(params);

                // PSP
                if (params.colDef.field.indexOf('PSPFO') > -1 && pspUrl != '' && params.data[params.colDef.field] != '')
                    window.open(pspUrl + encodeURIComponent(params.data[params.colDef.field]), "_blank");

                // PSP NOT WORKING ON DEV
                else if (params.colDef.field.indexOf('PSPFO') > -1)
                    toast.warn(translate('pspNotWorkingInDev', 'toast'));
            }

            // #region ACTIONS

            if (params.colDef.field == 'edit')
                getUserAdminDetail(params);

            if (params.colDef.field == 'delete')
                deleteUserAdmin(params);

            // #endregion
        }
    }

    function endLoadUsers(gridApi) {
        gridApi.setColumnGroupOpened('1', true);
        buttons.refresh.disabled = false;
        getLastCustomerAgUsersUpdate();
    }

    function refineGridColumns(gridApi, isDone) {
        var customerMode = customer.selector.selectedItem != null && customer.selector.selectedItem.name != ''
            && customer.selector.selectedItem.name != 'EXTERNIS' && distributor.selector.selectedItem === null;
        var nbASSFO = 0;
        var nbASSBO = 0;
        var nbPSPFO = 0;
        if(!gridApi.isDestroyed()){
            if (customerMode) {
                gridApi.setGridOption(['columnDefs'], customerColumnDefs);

                gridApi.forEachLeafNode((node, index) => {
                    if (node.data.tokenASSFO != '')
                        nbASSFO++;

                    if (node.data.tokenASSBO != '')
                        nbASSBO++;

                    if (node.data.tokenPSPFO != '')
                        nbPSPFO++;
                });
                if (nbASSFO != 0)
                    gridApi.setColumnsVisible(['tokenASSFO'], true);

                if (nbASSBO != 0)
                    gridApi.setColumnsVisible(['tokenASSBO'], true);

                if (nbPSPFO != 0)
                    gridApi.setColumnsVisible(['tokenPSPFO'], true);
            }
            else {
                gridApi.setGridOption(['columnDefs'], distributorColumnDefs);
            }
        }
    }

    function isEmailAvailable(userEmail) {
        return !(usersAdmin.value.filter(d => d.email == userEmail).length > 0 && usersAdmin.value.map(d => d.email).indexOf(userEmail) > -1 && usersAdmin.value.filter(d => d.email == userEmail).first().id != userAdminDialog.idSelected);
    }

    function checkEmailValidation() {
        var userEmail = userAdminDialog.email;

        if (!isEmailAvailable(userEmail)) {
            toast.warn(translate('mailAlreadyTaken', 'toast'));
            return false;
        }

        return true;
    }

    // #endregion

    // #region GET

    function getCustomers() {
        let data = {
            token: userStore.getUser().token,
            distributorId: userStore.getUser().distributorId,
        };

        return $.getData(urlWebAPI + 'GetCustomers', data)
            .then((data) => {
                if (userStore.isAdmin())
                    data.unshift({ name: 'EXTERNIS', reference: 'EXTERNIS' });

                customers.value = data;
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetCustomers data.');
            });
    }

    function getDistributors() {

        let data = {
            token: userStore.getUser().token,
        };

        return $.getData(urlWebAPI + 'GetDistributors', data)
            .then((data) => {
                distributors.value = data;
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetDistributors data.');
            });
    }

    function getLastCustomerAgUsersUpdate() {
        let data = {
            customerReference: customer.selector.selectedItem?.name,
            distributorId: distributor.selector.selectedItem != null ? parseInt(distributor.selector.selectedItem.id) : null,
        };

        $.getData(urlWebAPI + 'GetLastCustomerAgUsersUpdate', data)
            .then((lastUpdate) => {
                let formatedLastUpdate = new Date(lastUpdate);

                if (formatedLastUpdate.getFullYear() > 1900) {
                    let stringDate = formatedLastUpdate.toLocaleDateString() + ' ' + formatedLastUpdate.toLocaleTimeString();
                    toast.info(translate('lastUpdate', 'tooltip') + '\n' + stringDate);
                }
                else
                    console.log(translate('updateInProgress', 'tooltip'));
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetLastCustomerAgUsersUpdate data.')
            });
    }

    async function getUserAdminDetail(params) {

        let data = {
            token: userStore.getUser().token,
            distributorId: controlStore.controls.distributorSelect != null ? parseInt(controlStore.controls.distributorSelect.value) : null,
            userId: params.data.id,
        };

        $.getData(urlWebAPI + 'GetUserAdminDetail', data)
            .then((data) => {
                editUserAdmin(data);
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetUserAdminDetail data.');
            });
    }

    // #endregion

    // #region UPDATE
    function createUserAdmin() {
        userAdminDialog.idSelected = 0;

        // FIELDS
        userAdminDialog.userName = '';
        userAdminDialog.firstName = '';
        userAdminDialog.lastName = '';
        userAdminDialog.email = '';
        userAdminDialog.newPassword = '';
        userAdminDialog.confirmNewPassword = '';

        userAdminDialog.visible = true;
    }

    function editUserAdmin(userAdmin) {
        userAdminDialog.idSelected = userAdmin.id;

        // FIELDS
        userAdminDialog.userName = userAdmin.username;
        userAdminDialog.firstName = userAdmin.firstName;
        userAdminDialog.lastName = userAdmin.lastName;
        userAdminDialog.email = userAdmin.email;
        userAdminDialog.newPassword = '';
        userAdminDialog.confirmNewPassword = '';

        userAdminDialog.visible = true;
    }

    function saveUsers() {
        var data = data = {
            token: userStore.getUser().token,
            customerReference: customer.selector.selectedItem.name,
            agUsers: rowsUpdated.value,
        };

        $.postData(urlWebAPI + 'SaveUserInfos', data)
            .then((users) => {
                rowsUpdated.value = [];

                var gridApi = userGridRef.value.gridRef.api;
                var rowsToUpdate = [];

                gridApi.forEachLeafNode((node, index) => {
                    if (users.map(u => u.id).indexOf(node.data.id) > -1) {
                        node.data.userName = users.filter(u => u.id == node.data.id).first().userName;
                        rowsToUpdate.push(node.data);
                    }
                });

                gridApi.applyTransaction({
                    update: rowsToUpdate,
                });

                gridApi.flashCells({
                    rowNodes: rowsToUpdate,
                });

                toast.success(translate('userInfoWellSaved', 'toast'));

                setTimeout(() => {
                    toast.info(translate('refreshCustomerAfterUpdate', 'toast'));
                }, 3500);

            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing SaveUserInfos data.');
            });
    }

    function saveUserAdminDetail(event) {
        let data = {
            token: userStore.getUser().token,
            customerReference: customer.selector.selectedItem != null && customer.selector.selectedItem.name == 'EXTERNIS' ? 'EXTERNIS' : null,
            distributorId: distributor.selector.selectedItem != null ? parseInt(distributor.selector.selectedItem.id) : 0,
            userAdmin: {
                id: userAdminDialog.idSelected,
                username: userAdminDialog.userName,
                firstName: userAdminDialog.firstName,
                lastName: userAdminDialog.lastName,
                email: userAdminDialog.email,
                password: userAdminDialog.newPassword,
            },
        };
        $.postData(urlWebAPI + 'SaveUserAdminDetail', data)
            .then((data) => {
                toast.success(translate('userInfoWellSaved', 'toast'))

                userAdminDialog.visible = false;
                userRefresh.value = true;

                // RESET REFRESH
                setTimeout(() => {
                    userRefresh.value = false;
                }, 1000);
            })
            .catch(error => {
                logStore.log(err, 'error');
                toast.error('Error during executing SaveUserAdminDetail.');
            });
    }

    function deleteUserAdmin(params) {
        let data = {
            token: userStore.getUser().token,
            distributorId: distributor.selector.selectedItem != null ? parseInt(distributor.selector.selectedItem.id) : null,
            userId: params.data.id,
        };

        $.getData(urlWebAPI + 'DeleteUserAdmin', data)
            .then((data) => {
                toast.success(translate('userInfoWellSaved', 'toast'))
                userRefresh.value = true;

                // RESET REFRESH
                setTimeout(() => {
                    userRefresh.value = false;
                }, 1000);
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing DeleteUserAdmin.');
            });
    }

    // #endregion

    function portalRedirect(params) {
        var userId = params.data.id;

        let data = {
            token: userStore.getUser().token,
            customerReference: customer.selector.selectedItem.name,
            userId: userId,
        };

        $.getData(urlWebAPI + 'GetUserPortalAccess', data)
            .then((token) => {
                window.open(portalUrl.replace('auth.aspx', '') + '?a=ECOS&u=' + encodeURIComponent(token) + '&i=' + customer.selector.selectedItem.name + '&origin=MO', "_blank");

                // TODO: update the DEV WebApp to make a post redirect instead of send parameters in URL.
                //$.redirectPost(portalUrl, {
                //    a: 'ECOS',
                //    u: token,
                //    i: controlStore.controls.customerSelect.value,
                //});
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing GetCustomers data.');
            });


    }

    function refreshUserCache() {
        //TODO ?
    }
</script>
